import React, { useState, useEffect, useRef } from "react";
import {
  Menu,
  ChevronDown,
  User,
  LogOut,
  Settings,
  MessageSquare,
} from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { usePhoneAccount } from "../../contexts/PhoneAccountContext";
import SubscriptionStatus from "../SubscriptionStatus";
import { logout } from "../../utils/auth";
import { getApiBaseUrl } from "../../config/apiBase";
import { getEmbeddedSignupLoginOptions, buildEmbeddedSignupCallbackBodyAsync, installEmbeddedSignupMessageListener, clearEmbeddedSignupSession } from "../../config/facebookEmbeddedSignup";

const API_BASE = getApiBaseUrl();

const Header = ({ toggleSidebar }) => {
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [userName, setUserName] = useState("User");
  const [userInitial, setUserInitial] = useState("U");
  const [pageTitle, setPageTitle] = useState("Dashboard");
  const [pageSubtitle, setPageSubtitle] = useState("");
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [whatsappLoading, setWhatsappLoading] = useState(false);
  const [whatsappConnected, setWhatsappConnected] = useState(false);
  const dropdownRef = useRef(null);

  // Get phone accounts for Connect WhatsApp button visibility
  const { 
    phoneAccounts: wabaAccounts, 
    fetchPhoneAccounts: fetchContextPhoneAccounts
  } = usePhoneAccount();

  const location = useLocation();
  const navigate = useNavigate();

  // Update page title
  useEffect(() => {
    const path = location.pathname;
    if (path.includes("dashboard")) {
      setPageTitle("Dashboard");
      setPageSubtitle("Overview of your messaging analytics");
    } else if (path.includes("waba")) {
      setPageTitle("Whatsapp Numbers");
      setPageSubtitle("Manage your WhatsApp Numbers");
    } else if (path.includes("templates")) {
      setPageTitle("Templates");
      setPageSubtitle("Create and manage your WhatsApp message templates");
    } else if (path.includes("broadcast")) {
      setPageTitle("Campaigns");
      setPageSubtitle("Manage and track your WhatsApp campaigns");
    } else if (path.match(/\/campaigns\/\d+\/analytics/)) {
      setPageTitle("Campaign Analytics");
      setPageSubtitle("Delivery performance and contact breakdown");
    } else if (path.includes("campaigns")) {
      setPageTitle("Campaigns");
      setPageSubtitle("Manage and track your WhatsApp campaigns");
    } else if (path.includes("flow-list")) {
      setPageTitle("Flow Lists");
      setPageSubtitle("Manage your automation flows");
    } else if (path.includes("contacts") || path.includes("contact-lists")) {
      setPageTitle("Contacts");
      setPageSubtitle("Centralized contact universe for each WhatsApp phone");
    } else if (path.includes("live-chats")) {
      setPageTitle("Live Chats");
      setPageSubtitle("Manage real-time conversations with your customers");
    } else if (path.includes("download-center")) {
      setPageTitle("Download Center");
      setPageSubtitle("View and download your campaign log files");
    } else if (path.includes("profile")) {
      setPageTitle("Your Profile");
      setPageSubtitle("Manage your account settings");
    } else if (path.includes("settings")) {
      setPageTitle("Settings");
      setPageSubtitle("Configure your preferences");
    } else {
      setPageTitle("Dashboard");
      setPageSubtitle("");
    }
  }, [location]);

  useEffect(() => {
    // Try to get user name from multiple sources
    let name = null;
    
    // 1. First try localStorage "user" object
    const userData = localStorage.getItem("user");
    if (userData) {
      try {
        const parsedUser = JSON.parse(userData);
        if (parsedUser.name) {
          name = parsedUser.name;
        }
      } catch (e) {
        console.error("Error parsing user data:", e);
      }
    }
    
    // 2. If no name found, try to decode from JWT token
    if (!name) {
      const token = localStorage.getItem("token");
      if (token) {
        try {
          const payload = JSON.parse(atob(token.split('.')[1]));
          if (payload.name) {
            name = payload.name;
          } else if (payload.email) {
            // Fallback to email if no name
            name = payload.email.split('@')[0];
          }
        } catch (e) {
          console.error("Error decoding token:", e);
        }
      }
    }
    
    // 3. Set the name if found
    if (name) {
      setUserName(name);
      setUserInitial(name.charAt(0).toUpperCase());
    }
  }, []);

  const toggleUserMenu = () => {
    setShowUserMenu((v) => !v);
  };

  const handleLogout = () => {
    logout();
  };

  const handleRefresh = () => {
    setIsRefreshing(true);
    window.location.reload();
    setTimeout(() => setIsRefreshing(false), 2000);
  };

  // Update WhatsApp connection status based on phone accounts from context
  useEffect(() => {
    setWhatsappConnected(wabaAccounts.length > 0);
  }, [wabaAccounts]);

  const checkWhatsappConnection = async () => {
    // Refresh phone accounts from context
    if (fetchContextPhoneAccounts) {
      await fetchContextPhoneAccounts();
    }
  };

  const sendCodeToBackend = async (code) => {
    const token =
      localStorage.getItem("token") || localStorage.getItem("userAccessToken");
    try {
      setWhatsappLoading(true);

      const body = await buildEmbeddedSignupCallbackBodyAsync(code);
      if (body.cancelled) {
        setWhatsappLoading(false);
        clearEmbeddedSignupSession();
        return;
      }

      const res = await fetch(
        `${getApiBaseUrl()}/auth/facebook/embedded-signup/callback`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(body),
          credentials: "include",
        }
      );
      
      clearEmbeddedSignupSession();
      
      const data = await res.json();
      
      if (fetchContextPhoneAccounts) {
        await fetchContextPhoneAccounts();
      }
      
      window.dispatchEvent(new CustomEvent("phoneAccountsUpdated"));
      
      setWhatsappLoading(false);
      
      if (data.success) {
        const processedCount = data.processed?.length || 0;
        if (data.partial && data.webhookFailures?.length) {
          alert(
            `✅ ${processedCount} number(s) added. Some webhook registrations failed — use Retry on WhatsApp Business page.${data.action_required ? ` ${data.action_required}` : ""}`
          );
        } else {
          alert(`✅ ${processedCount} WhatsApp number(s) added successfully!`);
        }
      } else {
        alert(data.message || data.error || "Failed to complete onboarding. Please try again.");
      }
    } catch (error) {
      console.error("Failed to send signup code to backend:", error);
      setWhatsappLoading(false);
      clearEmbeddedSignupSession();
      alert("Failed to complete onboarding. Please try again.");
    }
  };

  const handleEmbeddedSignup = () => {
    clearEmbeddedSignupSession();
    setWhatsappLoading(true);

    const token =
      localStorage.getItem("token") || localStorage.getItem("userAccessToken");
    if (!token) {
      alert("⚠️ Please log in to your account first.");
      setWhatsappLoading(false);
      return;
    }

    if (window.FB && typeof window.FB.login === "function") {
      window.FB.login(
        (response) => {
          if (response.authResponse && response.authResponse.code) {
            sendCodeToBackend(response.authResponse.code);
          } else {
            setWhatsappLoading(false);
            alert("Failed to get authorization code. Please try again.");
          }
        },
        getEmbeddedSignupLoginOptions()
      );
    } else {
      setWhatsappLoading(false);
      alert("Facebook SDK not loaded. Please refresh and try again.");
    }
  };

  useEffect(() => {
    checkWhatsappConnection();

    const handleAccountUpdate = () => {
      if (fetchContextPhoneAccounts) {
        fetchContextPhoneAccounts();
      }
    };

    const removeSignupListener = installEmbeddedSignupMessageListener();

    window.addEventListener("phoneAccountsUpdated", handleAccountUpdate);
    window.addEventListener("wabaAccountsUpdated", handleAccountUpdate);

    return () => {
      removeSignupListener();
      window.removeEventListener("phoneAccountsUpdated", handleAccountUpdate);
      window.removeEventListener("wabaAccountsUpdated", handleAccountUpdate);
    };
  }, [fetchContextPhoneAccounts]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowUserMenu(false);
      }
    };
    if (showUserMenu) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showUserMenu]);

  return (
    <header className="fixed top-0 right-0 left-0 lg:left-[104px] z-30">
      {/* Header Container with Padding */}
      <div className="px-3 py-3 lg:pr-3">
        {/* Header Content */}
        <div className="rounded-[24px] h-14 md:h-16 px-3 md:px-5 flex items-center justify-between shadow-lg bg-gray-900 border border-gray-800">
          {/* Left: menu + title */}
          <div className="flex items-center gap-2 md:gap-3 min-w-0 flex-1 overflow-hidden">
            <button
              onClick={toggleSidebar}
              className="lg:hidden p-2 rounded-xl text-white/70 hover:text-white hover:bg-white/10 transition-all duration-200 flex-shrink-0"
              aria-label="Toggle sidebar"
            >
              <Menu className="w-5 h-5" />
            </button>

            <div className="min-w-0 flex-1 overflow-hidden">
              <h1 className="text-sm md:text-base lg:text-lg font-bold text-white truncate">
                {pageTitle}
              </h1>
              {pageSubtitle && (
                <p className="hidden sm:block text-[10px] md:text-xs text-white/50 truncate mt-0.5">
                  {pageSubtitle}
                </p>
              )}
            </div>
          </div>

          {/* Right: actions */}
          <div className="flex items-center gap-1.5 md:gap-2 flex-shrink-0">
            {/* Subscription Status */}
            <SubscriptionStatus />
            
            {/* WhatsApp Connect Button */}
            {!whatsappConnected && (
              <button
                onClick={handleEmbeddedSignup}
                disabled={whatsappLoading}
                className="flex items-center justify-center gap-1.5 md:gap-2 px-3 md:px-4 py-2 text-xs font-semibold text-gray-900 rounded-xl transition-all duration-200 shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ backgroundColor: "#eefa94" }}
                title="Connect WhatsApp Business"
              >
                {whatsappLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-3.5 w-3.5 border-2 border-gray-900 border-t-transparent"></div>
                    <span className="hidden sm:inline">Connecting...</span>
                  </>
                ) : (
                  <>
                    <MessageSquare size={14} className="md:w-4 md:h-4" />
                    <span className="hidden sm:inline">Connect WhatsApp</span>
                  </>
                )}
              </button>
            )}

            {/* User menu */}
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={toggleUserMenu}
                className="flex items-center gap-1.5 md:gap-2 rounded-xl bg-white/10 hover:bg-white/20 pl-1 md:pl-1.5 pr-2.5 md:pr-3 py-1.5 border border-white/20 transition-all duration-200"
                aria-haspopup="menu"
                aria-expanded={showUserMenu}
              >
                <div
                  className="w-7 h-7 md:w-8 md:h-8 rounded-full font-bold flex items-center justify-center text-xs md:text-sm flex-shrink-0 text-gray-900"
                  style={{ backgroundColor: "#eefa94" }}
                >
                  {userInitial}
                </div>
                <span className="hidden sm:block text-xs font-semibold text-white truncate max-w-16 md:max-w-24">
                  {userName}
                </span>
                <ChevronDown className="w-3.5 h-3.5 md:w-4 md:h-4 text-white/70 flex-shrink-0" />
              </button>

              {showUserMenu && (
                <div
                  role="menu"
                  className="absolute right-0 mt-2 w-52 md:w-56 rounded-2xl border border-gray-700 shadow-2xl overflow-hidden bg-gray-900"
                >
                  <div className="px-4 py-3 border-b border-gray-700 bg-gray-800">
                    <p className="text-sm font-bold text-white truncate">
                      {userName}
                    </p>
                    <p className="text-xs text-white/60 mt-0.5 font-medium">
                      Account User
                    </p>
                  </div>
                  <ul className="py-1">
                    <li>
                      <button
                        onClick={() => {
                          navigate("/profile");
                          setShowUserMenu(false);
                        }}
                        className="w-full text-left flex items-center gap-3 px-4 py-2.5 text-sm font-medium text-white/70 hover:text-white hover:bg-white/5 transition-all duration-200"
                        role="menuitem"
                      >
                        <User className="w-4 h-4" />
                        Your Profile
                      </button>
                    </li>
                    <li>
                      <button
                        onClick={() => {
                          navigate("/settings");
                          setShowUserMenu(false);
                        }}
                        className="w-full text-left flex items-center gap-3 px-4 py-2.5 text-sm font-medium text-white/70 hover:text-white hover:bg-white/5 transition-all duration-200"
                        role="menuitem"
                      >
                        <Settings className="w-4 h-4" />
                        Settings
                      </button>
                    </li>
                    <li className="border-t border-gray-700 mt-1">
                      <button
                        onClick={handleLogout}
                        className="w-full text-left flex items-center gap-3 px-4 py-2.5 text-sm font-semibold text-red-400 hover:text-red-300 hover:bg-red-500/10 transition-all duration-200"
                        role="menuitem"
                      >
                        <LogOut className="w-4 h-4" />
                        Sign Out
                      </button>
                    </li>
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      
      {/* Full-screen loading overlay during embedded signup processing */}
      {whatsappLoading && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[9999] flex items-center justify-center">
          <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full mx-4 flex flex-col items-center gap-6">
            <div className="relative">
              <div className="animate-spin rounded-full h-16 w-16 border-4 border-gray-200 border-t-[#17181b]"></div>
            </div>
            <div className="text-center">
              <h3 className="text-xl font-bold text-gray-900 mb-2">
                Processing WhatsApp Setup
              </h3>
              <p className="text-sm text-gray-600 mb-4">
                Please wait while we configure your WhatsApp Business account...
              </p>
              <div className="space-y-2 text-xs text-gray-500">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-[#17181b] animate-pulse"></div>
                  <span>Exchanging authentication token</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-[#17181b] animate-pulse"></div>
                  <span>Fetching phone numbers</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-[#17181b] animate-pulse"></div>
                  <span>Registering webhooks</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-[#17181b] animate-pulse"></div>
                  <span>Completing setup</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
